import type { FC, MouseEvent, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { engine } from '@/lib/motion/engine'
import { LogoMark } from '@/components/landing/primitives'

import {
    getAppState,
    setAppState,
    useAppState
} from '@/lib/app-store'

const formatGmt = (date: Date): string => {
    const hours = date.getUTCHours()
    const minutes = date.getUTCMinutes()
    const suffix = hours >= 12 ? 'pm' : 'am'
    const display = hours % 12 || 12

    return `${display}:${String(minutes).padStart(2, '0')} ${suffix}`
}

const SiteHeader: FC = (): ReactNode => {
    const [time, setTime] = useState('')
    const menuOpen = useAppState(state => state.menuOpen)

    useEffect(() => {
        const update = (): void => setTime(formatGmt(new Date()))

        update()

        const id = window.setInterval(update, 1000)

        return () => window.clearInterval(id)
    }, [])

    const toggleMenu = (): void => {
        setAppState({ menuOpen: !getAppState().menuOpen })
    }

    const goTop = (event: MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault()
        setAppState({ menuOpen: false })
        engine.scrollTo(0, { duration: 2.8, force: true })
    }

    return (
        <header className='fixed inset-x-0 top-0 z-50'>
            <div className='relative flex h-14 items-center justify-between border-b border-line/70 bg-white/60 px-4 backdrop-blur-md backdrop-saturate-150 md:px-6'>
                <a href='#hero' onClick={goTop} aria-label='Orvane, back to top' className='flex items-center gap-2.5'>
                    <LogoMark className='size-[18px] text-ink' />

                    <span className='text-[15px] font-medium tracking-[-0.03em] text-ink'>
                        orvane
                    </span>

                    <span aria-hidden='true' className='size-1 rounded-full bg-ink/35' />

                    <span className='hidden text-[13px] tracking-[-0.01em] text-haze sm:inline'>
                        Lidar Drone Scanning
                    </span>
                </a>

                <p className='pointer-events-none absolute left-1/2 hidden -translate-x-1/2 text-[13px] tracking-[-0.01em] text-ink md:block'>
                    Precision Demo
                </p>

                <div className='flex items-center gap-3 md:gap-5'>
                    <p className='hidden min-w-[96px] text-right font-mono text-[11px] uppercase tracking-[0.08em] text-ink sm:block'>
                        <span>GMT</span>{' '}
                        <time className='normal-case tabular-nums'>
                            {time || '--:--'}
                        </time>
                    </p>

                    <button
                        type='button'
                        onClick={toggleMenu}
                        aria-expanded={menuOpen}
                        aria-controls='site-menu'
                        className='h-7 min-w-[62px] rounded-full bg-[#e4e9eb] px-3.5 text-[13px] tracking-[-0.01em] text-ink transition-colors duration-300 hover:bg-[#d7dee1]'
                    >
                        {menuOpen ? 'close' : 'menu'}
                    </button>
                </div>
            </div>
        </header>
    )
}

export default SiteHeader
